import { useCallback, useEffect, useRef } from "react";

/** Distance from the bottom that still counts as "caught up". */
export const NEAR_BOTTOM_THRESHOLD_PX = 100;
export const CAUGHT_UP_DELAY_MS = 3000;

/**
 * Reports that the reader has settled at the bottom of the list: near the
 * bottom continuously for `delayMs`. Scrolling away cancels the pending
 * report; coming back restarts the wait, so reading back through history
 * never trips it.
 *
 * Returns a `report` to call with the current distance from the bottom —
 * from the list's scroll handler, and once after mount so a list that opens
 * already at the bottom arms the timer without waiting for a scroll.
 */
export const useCaughtUpNearBottom = ({
  onCaughtUp,
  delayMs = CAUGHT_UP_DELAY_MS,
}: {
  onCaughtUp?: () => void;
  delayMs?: number;
}): { reportBottomOffset: (bottomOffset: number) => void } => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFiredRef = useRef(false);

  // Read through a ref so a caller passing an inline arrow doesn't restart
  // the timer on every render.
  const onCaughtUpRef = useRef(onCaughtUp);
  onCaughtUpRef.current = onCaughtUp;

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const reportBottomOffset = useCallback(
    (bottomOffset: number) => {
      if (hasFiredRef.current || !onCaughtUpRef.current) return;

      const isNearBottom = bottomOffset < NEAR_BOTTOM_THRESHOLD_PX;

      if (!isNearBottom) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      // Already counting down — let it run rather than restarting on every
      // scroll tick, which would keep pushing the deadline out.
      if (timerRef.current) return;

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        hasFiredRef.current = true;
        onCaughtUpRef.current?.();
      }, delayMs);
    },
    [delayMs]
  );

  return { reportBottomOffset };
};
